import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Code, Copy, Check, ExternalLink, KeyRound, Bookmark, BookOpen, Rocket } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";

/** Public developer portal — surfaces the OpenAPI ReDoc reference (which
 *  lives at /api/redoc) inside Pick2Play's dark shell, with a quick-start
 *  curl snippet, common-task pointers, and a link to the raw spec for SDK
 *  generation.
 *
 *  Designed as an entry point for third-party integrations (federation
 *  websites, club management platforms, fitness trackers).
 */
export default function DevelopersPortal() {
  const apiBase = useMemo(() => (typeof window !== "undefined" ? window.location.origin : ""), []);
  const [copied, setCopied] = React.useState(null);

  useEffect(() => { document.title = "Pick2Play — Developer API"; }, []);

  const copy = async (id, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 1500); toast.success("Copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const curlSnippet = `curl -s "${apiBase}/api/sports"`;
  const sdkSpec = `${apiBase}/api/openapi.json`;

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="developers-portal">
        {/* Hero */}
        <div className="pp-card-elevated p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#00E676]">
            <Code size={14} /> Developer API
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black mt-3">Build on Pick2Play.</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            REST API · OpenAPI 3.1 · 151 endpoints across 17 domains. Power your
            federation website, club management dashboard, or fitness app with
            real Pick2Play data — players, ratings, tournaments, results, and venues.
          </p>
          <div className="mt-6 flex items-center flex-wrap gap-3">
            <a href="/api/redoc" target="_blank" rel="noreferrer"
               data-testid="dev-redoc-link"
               className="pp-btn-primary px-5 py-3 rounded-xl flex items-center gap-2">
              <BookOpen size={16} /> Read the reference
            </a>
            <a href="/api/docs" target="_blank" rel="noreferrer"
               data-testid="dev-swagger-link"
               className="pp-btn-secondary px-5 py-3 rounded-xl flex items-center gap-2">
              <Rocket size={16} /> Try in Swagger UI <ExternalLink size={12} />
            </a>
            <a href={sdkSpec} target="_blank" rel="noreferrer"
               data-testid="dev-spec-link"
               className="pp-btn-secondary px-5 py-3 rounded-xl flex items-center gap-2">
              <Bookmark size={16} /> Download OpenAPI spec
            </a>
          </div>
        </div>

        {/* Quick-start */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 pp-card p-6 space-y-3">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Rocket size={16} className="text-[#FFD60A]" /> 30-second quick-start
            </h2>
            <p className="text-zinc-400 text-sm">
              Most read endpoints are public. The example below lists every sport seeded
              on the platform — no auth, no rate limit, JSON in the response body.
            </p>
            <div className="pp-card bg-black/40 p-4 font-mono text-sm flex items-start justify-between gap-3 border border-white/5">
              <code data-testid="dev-curl-snippet" className="text-[#00E676] break-all">{curlSnippet}</code>
              <button onClick={() => copy("curl", curlSnippet)}
                      data-testid="dev-curl-copy"
                      className="text-zinc-400 hover:text-white shrink-0 mt-0.5">
                {copied === "curl" ? <Check size={14} className="text-[#00E676]" /> : <Copy size={14} />}
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              Authenticated endpoints accept either an <code className="text-zinc-300">httpOnly</code> session
              cookie (browser flow via <code className="text-zinc-300">POST /api/auth/login</code>) or
              an <code className="text-zinc-300">Authorization: Bearer …</code> header from{" "}
              <code className="text-zinc-300">POST /api/auth/login-mobile</code>.
            </p>
          </div>

          <div className="pp-card p-6 space-y-3">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <KeyRound size={16} className="text-[#007AFF]" /> Auth at a glance
            </h2>
            <ul className="space-y-2 text-sm">
              {[
                ["Cookie session", "Browser apps. Use /api/auth/login, then send credentials with every request."],
                ["Bearer token", "Mobile / native. POST /api/auth/login-mobile returns {token}; pass as Authorization header."],
                ["Public endpoints", "GET /api/sports, /api/clubs, /api/tournaments, /api/facilities — no auth needed."],
              ].map(([label, body]) => (
                <li key={label}>
                  <div className="font-semibold">{label}</div>
                  <div className="text-zinc-500 text-xs">{body}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Domain map */}
        <div className="pp-card p-6">
          <h2 className="font-heading text-xl font-bold mb-4">17 API domains</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
            {[
              ["Auth", "Login, signup, OAuth, refresh"],
              ["Clubs", "Directory + CRUD + members"],
              ["Tournaments", "Multi-class, classes, brackets"],
              ["Matches", "Open match join + results"],
              ["Leagues", "Standings, schedule, points"],
              ["Lessons", "Coach scheduling + pay"],
              ["Coach Students", "Enrollment QR + roster"],
              ["Coach Messages", "Broadcast + inbox"],
              ["Events", "Share QR + ICS calendar"],
              ["Facilities", "Public venue map (5,300+)"],
              ["Golf", "Stableford / Ryder Cup PDFs"],
              ["Player Passport", "Public ELO + history"],
              ["Push Notifications", "Web push + Expo push"],
              ["AI Recommendations", "Gemini-powered for-you"],
              ["Payments & ELO", "Stripe Connect splits"],
              ["Referrals", "Invite-and-earn loops"],
              ["Admin", "Operator C-panel"],
            ].map(([name, desc]) => (
              <div key={name} className="rounded-xl border border-white/5 p-3 hover:border-[#00E676]/40 transition-colors">
                <div className="font-semibold text-[#00E676]">{name}</div>
                <div className="text-zinc-500 text-xs mt-1">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Embedded reference */}
        <div className="pp-card overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <BookOpen size={16} /> Live API reference
            </h2>
            <a href="/api/redoc" target="_blank" rel="noreferrer"
               className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
              Open in new tab <ExternalLink size={11} />
            </a>
          </div>
          <iframe
            title="Pick2Play API reference"
            src="/api/redoc"
            data-testid="dev-redoc-iframe"
            className="w-full bg-white"
            style={{ height: "1200px", border: "0" }}
          />
        </div>

        <div className="text-center text-xs text-zinc-500">
          Need higher rate limits, SLA, or a webhook-based push integration?{" "}
          <Link to="/contact" className="underline hover:text-white">Talk to us.</Link>
        </div>
      </div>
    </AppLayout>
  );
}
